import type {ReactNode} from 'react';
import {useCallback, useRef} from 'react';
import {CSSTransition} from 'react-transition-group';
import classNames from 'classnames';
import {useEventListener} from 'usehooks-ts';
import {useHotkeys} from 'react-hotkeys-hook';
import {FocusScope} from '@react-aria/focus';
import {createPortal} from 'react-dom';

import {FilePreviewActionClose} from './file-preview-action-close';
import styles from './file-preview.module.css';

type Props = {

    /**
   * Заголовок диалога
   */
    title: ReactNode;

    /**
   * Скрытое описание диалога,
   * которое помогает создавать более доступный UI
   */
    description?: string;

    /**
   * Дополнительные CSS классы
   */
    className?: string;

    /**
   * Дополнительные кнопки действий
   */
    actions?: ReactNode;

    /**
   * Основной контент
   */
    children: ReactNode;

    /**
   * Управляет отображением диалога
   */
    show: boolean;

    /**
   * Коллбек, который срабатывает при закрытии диалога любым способом
   */
    onClose?: () => void;
};

export const FilePreview = ({show, className, actions, title, description, children, onClose}: Props) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const handleClose = useCallback(() => onClose?.(), [onClose]);
    const handleOverlayClick = useCallback(
        (evt: MouseEvent) => {
            if (evt.target === overlayRef.current) {
                handleClose();
            }
        },
        [handleClose],
    );

    useHotkeys<HTMLDivElement>('esc', handleClose);
    useEventListener('click', handleOverlayClick, overlayRef);

    const preview = (
        <CSSTransition nodeRef={dialogRef} timeout={{exit: 150, enter: 0, appear: 0}} in={show}>
            {(state: string) => (
                <FocusScope contain={true} autoFocus={true}>
                    <div
                        className={classNames(styles.root, styles.fade, className, {
                            [styles.hidden]: state === 'exited',
                            [styles.fadeIn]: state === 'entered',
                        })}
                        ref={dialogRef}
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='file-preview-title'
                        aria-describedby='file-preview-description'
                    >
                        <div className={classNames(styles.header, className)}>
                            <div className={styles.title} id='file-preview-title'>
                                {title}
                            </div>
                            <div className={styles.actions}>
                                <>
                                    {actions}
                                    <FilePreviewActionClose onClick={handleClose} />
                                </>
                            </div>
                        </div>
                        <div className={styles.description} id='file-preview-description'>
                            {description}
                        </div>
                        <div className={classNames(styles.overlay)} data-testid='overlay' ref={overlayRef}>
                            {children}
                        </div>
                    </div>
                </FocusScope>
            )}
        </CSSTransition>
    );

    return createPortal(preview, document.body);
};
